import React from 'react';
import {Button, TextInput} from '@gravity-ui/uikit';
import styles from './IdentifierForm.module.scss';
import {AuthMethodType, useIdentifierForm} from '../../model';
import {getSessionItem} from '../../../../shared';
import {useAuth} from '../../../../entities';

export type IdentifierFormProps = {
  authMethod: AuthMethodType;
  onSelectAuthMethod: (method: AuthMethodType) => void;
};

export const IdentifierForm = ({onSelectAuthMethod, authMethod}: IdentifierFormProps) => {
  const {values, errors, touched, handleChange, handleBlur, handleSubmit, isInitFlowLoading} = useIdentifierForm();
  const {requestCodeLogin} = useAuth();
  const isFormValid = values.identifier !== '' && !errors.identifier;
  const hasError = touched.identifier && Boolean(errors.identifier);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isFormValid) {
      handleSubmit();
      onSelectAuthMethod(authMethod);
    }
  };

  const handleAuthMethodSelect = async (method: AuthMethodType) => {
    handleSubmit();
    if (isFormValid) {
      if (method === 'code') {
        // Для метода code сразу отправляем запрос на получение кода
        try {
          const flowId = getSessionItem('auth_flow_id');
          const csrfToken = getSessionItem('auth_csrf_token');
          if (flowId && csrfToken) {
            await requestCodeLogin({
              identifier: values.identifier,
              flowId,
              csrfToken,
            });
          }
        } catch (error) {
          console.error('Failed to request code:', error);
        }
      }
      onSelectAuthMethod(method);
    }
  };

  const commonButtonProps = {
    size: 'xl' as const,
    width: 'max' as const,
    type: 'button' as const,
  };

  const authButtons = [
    {
      method: 'password' as const,
      view: 'action' as const,
      text: 'Войти с паролем',
    },
    {
      method: 'code' as const,
      view: 'normal' as const,
      text: 'Войти с кодом',
    },
  ];

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAuthMethodSelect('password');
    }
  };

  return (
    <form className={styles.formContainer} onSubmit={handleFormSubmit}>
      <TextInput
        placeholder="Имя пользователя или почта"
        size="xl"
        name="identifier"
        value={values.identifier}
        onChange={handleChange}
        onBlur={handleBlur}
        errorPlacement="inside"
        error={hasError}
        errorMessage={hasError && errors.identifier}
        onKeyDown={handleInputKeyDown}
        autoFocus
      />
      <div className={styles.authButtons}>
        {authButtons.map((button) => (
          <Button
            key={button.method}
            {...commonButtonProps}
            view={button.view}
            onClick={() => handleAuthMethodSelect(button.method)}
            loading={isInitFlowLoading && authMethod === button.method}>
            {button.text}
          </Button>
        ))}
      </div>
    </form>
  );
};
